class DesktopScrollGallery {
  constructor(container) {
    this.container = container;
    this.images = container.querySelectorAll('.desktop-scroll-gallery-image');
    this.thumbnails = container.querySelectorAll('.desktop-scroll-gallery-thumbnail-item');
    this.thumbnailsContainer = container.querySelector('.desktop-scroll-gallery-thumbnails-container');
    this.originalCount = this.images.length / 2;
    
    this.currentImageIndex = 0;
    this.isScrolling = false;
    this.isUserScrolling = false;
    this.scrollTimeout = null;
    this.galleryStartScroll = null;

    // CHANGE 1: dynamic step + manual-selection lock state
    this.step = 218; // safe default, replaced by recomputeStep()
    this.manualLockActive = false;

    this.init();
  }

  init() {
    if (this.images.length === 0 || this.thumbnails.length === 0) return;
    
    if (window.innerWidth < 990) return;
    
    this.setupEventListeners();
    this.recomputeStep(); // CHANGE 1: initial step calculation
    this.updateImageDisplay();
    this.setupIntersectionObserver();
    this.setupKeyboardNavigation();
  }

  // CHANGE 1: dynamic step derived from actual sticky runway
  recomputeStep() {
    const parent = this.container.parentElement;
    if (!parent || this.originalCount < 2) return;
    const parentHeight = parent.offsetHeight;
    const galleryHeight = this.container.offsetHeight;
    // runway = distance the sticky element can travel inside its parent
    const runway = Math.max(parentHeight - galleryHeight, this.originalCount * 60);
    this.step = runway / (this.originalCount - 1);
  }

  setupEventListeners() {
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.jumpToImage(index);
      });
    });

    if (this.thumbnailsContainer) {
      this.thumbnailsContainer.addEventListener('scroll', this.handleThumbnailScroll.bind(this), { passive: true });
    }

    window.addEventListener('resize', this.handleResize.bind(this));
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isInViewport = true;
        } else {
          this.isInViewport = false;
        }
      });
    }, {
      threshold: 0.1
    });

    observer.observe(this.container);
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (!this.isInViewport) return;
      
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        if (this.currentImageIndex < this.images.length - 1) {
          this.jumpToImage(this.currentImageIndex + 1);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        if (this.currentImageIndex > 0) {
          this.jumpToImage(this.currentImageIndex - 1);
        }
      }
    });
  }

  handleScroll() {
    if (this.isScrolling || !this.isInViewport) return;
    
    this.isScrolling = true;
    this.isUserScrolling = true;
    
    requestAnimationFrame(() => {
      this.updateScrollPosition();
      this.isScrolling = false;
      
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.isUserScrolling = false;
      }, 150);
    });
  }

  updateScrollPosition() {
    const scrollTop = window.pageYOffset;
    const galleryRect = this.container.getBoundingClientRect();

    if (this.galleryStartScroll === null && galleryRect.top <= 90) {
      this.galleryStartScroll = scrollTop;
    }

    if (galleryRect.top <= 90 && galleryRect.bottom > 20 && this.galleryStartScroll !== null) {
      // CHANGE 2: respect manual selection lock while inside engagement zone
      if (this.manualLockActive) return;

      const relativeScroll = scrollTop - this.galleryStartScroll;
      // CHANGE 1: use dynamic step instead of fixed 218px
      const newImageIndex = Math.min(Math.floor(relativeScroll / this.step), this.originalCount - 1);

      if (newImageIndex !== this.currentImageIndex && newImageIndex >= 0) {
        this.currentImageIndex = newImageIndex;
        this.updateImageDisplay();
      }
    }

    if (galleryRect.top > 90 || galleryRect.bottom <= 20) {
      this.galleryStartScroll = null;
      // CHANGE 2: release manual lock when user leaves the engagement zone
      this.manualLockActive = false;
    }
  }


  updateImageDisplay() {
  this.images.forEach((img, index) => {
    const isActive = index === this.currentImageIndex;
    img.classList.toggle('active', isActive);

    const video = img.querySelector('video');
    if (video) {
      if (isActive) {
        video.play().catch(() => {}); // autoplay can be blocked; fail silently
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  });

  this.thumbnails.forEach((thumbnail, index) => {
    thumbnail.classList.toggle('active', index === this.currentImageIndex);
  });

  this.scrollToActiveThumbnail();
}

  scrollToActiveThumbnail() {
    if (!this.thumbnailsContainer || this.thumbnails.length === 0) return;

    const activeThumbnail = this.thumbnails[this.currentImageIndex];
    if (!activeThumbnail) return;

    const containerHeight = this.thumbnailsContainer.clientHeight;
    const thumbnailHeight = 109;
    const activeThumbnailTop = activeThumbnail.offsetTop;
    const activeThumbnailCenter = activeThumbnailTop + (thumbnailHeight / 2);
    const containerCenter = containerHeight / 2;
    
    const targetScrollTop = activeThumbnailCenter - containerCenter;
    
    this.thumbnailsContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
  }

  handleThumbnailScroll() {
    // Prevent interference with main scroll logic when user manually scrolls thumbnails
  }


  jumpToImage(index) {
    if (index < 0 || index >= this.images.length) return;

    this.currentImageIndex = index;
    // CHANGE 2: engage lock so scroll can't overwrite the user's selection
    this.manualLockActive = true;
    this.updateImageDisplay();
  }


  handleResize() {
    // CHANGE 1: keep step in sync with viewport
    this.recomputeStep();

    if (window.innerWidth >= 990 && !this.isInitialized) {
      this.isInitialized = true;
      this.init();
    } else if (window.innerWidth < 990 && this.isInitialized) {
      this.isInitialized = false;
      this.cleanup();
    }
  }

  cleanup() {
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    if (this.thumbnailsContainer) {
      this.thumbnailsContainer.removeEventListener('scroll', this.handleThumbnailScroll.bind(this));
    }
    
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const galleries = document.querySelectorAll('.desktop-scroll-gallery');
  
  galleries.forEach(gallery => {
    new DesktopScrollGallery(gallery);
  });
});

window.addEventListener('load', function() {
  const galleries = document.querySelectorAll('.desktop-scroll-gallery:not([data-initialized])');
  
  galleries.forEach(gallery => {
    gallery.setAttribute('data-initialized', 'true');
    new DesktopScrollGallery(gallery);
  });
});

window.DesktopScrollGallery = DesktopScrollGallery;